import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSellerAuth } from "@/context/SellerAuthContext";
import { formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Store, Loader2, Phone, Lock } from "lucide-react";

export default function SellerLogin() {
  usePageMeta("Seller Login", "Log in to your NICEE Seller Portal.");
  const { login } = useSellerAuth();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login({ mobile, password });
      navigate("/seller/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-4 py-10">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-3xl bg-white p-8 shadow-lift">
        <Link to="/" className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand text-ink">
          <Store className="h-6 w-6" />
        </Link>
        <h1 className="mt-5 text-center font-heading text-2xl font-bold text-ink">NICEE Seller Portal</h1>
        <p className="mt-1 text-center text-sm text-ink-soft">Log in to manage your shop</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-ink-soft">Mobile Number</Label>
            <div className="relative mt-1.5">
              <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile" className="rounded-xl pl-9" required data-testid="seller-login-mobile" />
            </div>
          </div>
          <div>
            <Label className="text-ink-soft">Password</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="rounded-xl pl-9" required data-testid="seller-login-password" />
            </div>
          </div>
          {error && <p className="text-sm text-destructive" data-testid="seller-login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="seller-login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Login"}
          </Button>
        </form>

        <div className="mt-4 flex items-center justify-between text-sm">
          <Link to="/seller/forgot-password" className="text-ink-soft hover:text-brand hover:underline" data-testid="seller-forgot-link">Forgot Password?</Link>
          <Link to="/seller/register" className="font-semibold text-brand hover:underline" data-testid="seller-register-link">Register as Seller</Link>
        </div>
      </motion.div>
    </div>
  );
}
